import { Stream } from '@/types/commons/commons.types';

import {
  CommonSensorStatus,
  CommonSubSensorConfiguration,
  HasHealthStatus,
  MetricStatusValue,
  SensorTrackingStatus,
  SpecificStateEnum,
  TrackingStatusEnum,
} from './common.types';

export interface CommonCameraConfiguration {
  vxMin: number;
  vxMax: number;
  vyMin: number;
  vyMax: number;
}

export interface SubCameraConfiguration extends CommonSubSensorConfiguration, Stream {
  cameraId: number;
  minViewAngle: number;
  maxViewAngle: number;
  resolutionX: number;
  resolutionY: number;
  effectiveTurnOn: string | null;
  effectiveTurnOff: string | null;
  automaticTurnOff: string | null;
  displayExtendTimePopup: boolean | null;
}

export interface CameraToken {
  userName: string;
  cameraOperatorUuid: string;
  controlType: ControlType;
}

export enum ControlType {
  STANDARD = 'STANDARD',
  PREMIUM = 'PREMIUM',
}

export interface VideoBox {
  x: number;
  y: number;
  height: number;
  width: number;
}

export interface DetectedObject {
  id: string | null;
  box: VideoBox;
}

export enum IsoModeEnum {
  // ISO_UNSPECIFIED = '0',
  // ISO_50 = '1',
  // ISO_64 = '2',
  // ISO_80 = '3',
  ISO_100 = '4',
  // ISO_125 = '5',
  // ISO_160 = '6',
  // ISO_200 = '7',
  // ISO_250 = '8',
  // ISO_320 = '9',
  // ISO_400 = '10',
  // ISO_500 = '11',
  // ISO_640 = '12',
  // ISO_800 = '13',
  ISO_1000 = '14',
  // ISO_1250 = '15',
  // ISO_1600 = '16',
  // ISO_2000 = '17',
  // ISO_2500 = '18',
  // ISO_3200 = '19',
  // ISO_4000 = '20',
  // ISO_5000 = '21',
  // ISO_6400 = '22',
  // ISO_8000 = '23',
  // ISO_10000 = '24',
  // ISO_12800 = '25',
  ISO_16000 = '26',
  // ISO_20000 = '27',
  // ISO_25600 = '28',
  ISO_32000 = '29',
  // ISO_40000 = '30',
  // ISO_51200 = '31',
  ISO_64000 = '32',
  // ISO_80000 = '33',
  // ISO_102400 = '34',
  ISO_128000 = '35',
  // ISO_160000 = '36',
  // ISO_204800 = '37',
  // ISO_256000 = '38',
  // ISO_320000 = '39',
  //ISO_409600 = '40',
  ISO_AUTO = '41',
  // ISO_MULTI_FLAME_NR_AUTO = '42',
  // ISO_MULTI_FLAME_NR_100 = '43',
  // ISO_MULTI_FLAME_NR_200 = '44',
  // ISO_MULTI_FLAME_NR_400 = '45',
  // ISO_MULTI_FLAME_NR_800 = '46',
  // ISO_MULTI_FLAME_NR_1600 = '47',
  // ISO_MULTI_FLAME_NR_3200 = '48',
  // ISO_MULTI_FLAME_NR_6400 = '49',
  // ISO_MULTI_FLAME_NR_12800 = '50',
  // ISO_MULTI_FLAME_NR_25600 = '51',
  // ISO_MULTI_FLAME_NR_51200 = '52',
  // ISO_MULTI_FLAME_NR_102400 = '53',
  // ISO_MULTI_FLAME_NR_204800 = '54',
  // ISO_MULTI_FLAME_NR_409600 = '55',
}

export enum WhiteBalanceModeEnum {
  WHITE_BALANCE_UNSPECIFIED = '0',
  WHITE_BALANCE_AUTO_WHITE_BALANCE = '1',
  WHITE_BALANCE_DAYLIGHT = '2',
  WHITE_BALANCE_INCANDESCENT = '3',
  WHITE_BALANCE_FLUSH = '4',
  WHITE_BALANCE_FLUOR_WARM_WHITE_m1 = '5',
  WHITE_BALANCE_FLUOR_COOL_WHITE_0 = '6',
  WHITE_BALANCE_FLUOR_DAY_WHITE_p1 = '7',
  WHITE_BALANCE_FLUOR_DAYLIGHT_p2 = '8',
  WHITE_BALANCE_CLOUDY = '9',
  WHITE_BALANCE_SHADE = '10',
  WHITE_BALANCE_COLOR_TEMP_FILTER = '11',
  WHITE_BALANCE_UNDERWATER_AUTO = '12',
}

export enum CameraIrAlgoEnum {
  UNKNOWN = '0',
  DRC = '1',
  TEMPORAL_FILTERING = '2',
  SPATIAL_FILTERING = '4',
  ENHANCEMENT = '8',
  SHARPENING = '16',
}

export enum CameraStateEnum {
  CONNECTED = '0',
  READY = '1',
  CONFIGURED = '2',
  TRACKING_PENDING = '3',
  TRACKING_ACTIVE = '4',
  TRACKING_LOST = '5',
  CLOSE = '6',
}

export enum SystemModeEnum {
  STANDBY = '0',
  MANUAL = '1',
  ACQUIRE = '2',
  TRACK = '3',
  SCAN = '4',
  PARK = '5',
  REMOTE_DESIGNATION = '6',
  WASH = '7',
  FACTORY = '8',
}

export enum TrackingTypeEnum {
  VIDEO,
  SENSOR,
}

export interface TrackerStatus {
  status: TrackingStatusEnum;
  isBufferized: boolean;
}

export enum VideoStateEnum {
  ON = 0,
  DEGRADED = 1,
  OFF = 2,
}

export enum ZoomModeEnum {
  MANUAL = '0',
  AUTO = '1',
}

export enum FocusModeEnum {
  MANUAL = 0,
  AUTO = 1,
}

export enum ExposureModeEnum {
  UNSET = 'UNSET',
  AUTO = 'AUTO',
  MANUAL = 'MANUAL',
  SHUTTER_PRIORITY = 'SHUTTER_PRIORITY',
  IRIS_PRIORITY = 'IRIS_PRIORITY',
  BRIGHT = 'BRIGHT',
}

export interface VideoStatus extends HasHealthStatus {
  id: number;
  state: VideoStateEnum | null;
  zoom: number | null;
  numericalZoom: number | null;
  opticalZoom: number | null;
  fov: number | null;
  focus: number | null;
  magnification: number | null;
  zoomMode: ZoomModeEnum | null;
  focusMode: FocusModeEnum | null;
  exposureMode: ExposureModeEnum | null;
  iso: IsoModeEnum | null;
  whiteBalance: WhiteBalanceModeEnum | null;
  blackHot: boolean;
  focusLevel: number | null;
  deIcing: boolean;
  algo: CameraIrAlgoEnum;
  gain: number;
  offset: number;
  gamma: number;
  bootLoader: string | null;
  softwareVersion: string | null;
  serialNumber: string | null;
  temperature: MetricStatusValue; // In celcius
  humidity: MetricStatusValue; // In Percents
  pressure: MetricStatusValue; // In hectopascals
  mainStreaming: boolean;
  subStreaming: boolean;
  streamerOn: boolean;
  runningTime: number | null; // In seconds
  workingHours: MetricStatusValue; // In hours
  powered: boolean;
  present: boolean;
  irOn: boolean;
}

export interface CameraTrackingStatus {
  globalTrackingType: TrackingTypeEnum | null;
  globalTrackingState: TrackingStatusEnum;
  videoTrackingStatus: VideoTrackingStatus;
  sensorTrackingStatus: SensorTrackingStatus;
}

export interface VideoTrackingStatus {
  detectedObjects: DetectedObject[];
  holopticsVideoTrackings: Partial<Record<number, TrackerStatus>>;
  detectionZone: VideoBox | null;
  currentTrackedObjectId: string | null;
  globalVideoTrackingState: TrackingStatusEnum;
}

export type CameraSpecificStateEnum = SpecificStateEnum.ROUND | SpecificStateEnum.TRACKING;

export interface CommonSensorCameraStatus extends CommonSensorStatus {
  state: CameraStateEnum;
  systemMode: SystemModeEnum;
  cameraToken: CameraToken | null;
  pan: number | null;
  tilt: number | null;
  roundOn: boolean;
  simpleRoundSpeed: number | null;
  trackingStatus: CameraTrackingStatus;
  videoStatuses: VideoStatus[];
  specificState: CameraSpecificStateEnum | null;
}

export type SubCameraLiteConfiguration = {
  name: string | null;
  uniqueKey: string;
  streamKey: string;
  sensorId: number;
  site: string;
  appCode: string;
};

export type CameraLiteConfiguration = {
  name: string | null;
  uniqueKey: string;
  subCameras: SubCameraLiteConfiguration[];
};
